import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
} from '@fluentui/react-components';
import { Dismiss24Regular, PersonAccounts20Regular, HatGraduation20Regular } from '@fluentui/react-icons';
import type { EducationClass, EducationUser, EducationTeam, TeamOperation, PrimaryChannel, ChannelTab, EducationSchool } from '../../types';
import educationService from '../../services/educationService';
import { EmptyState } from '../Common/EmptyState';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '950px',
    width: '90vw',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    padding: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorBrandForeground1,
    marginBottom: '8px',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
  fieldLabel: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    marginBottom: '4px',
  },
  fieldValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
  },
  table: {
    backgroundColor: tokens.colorNeutralBackground1,
    marginTop: '8px',
    tableLayout: 'fixed',
    width: '100%',
  },
  emptyMessage: {
    padding: '16px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

interface ClassDetailsProps {
  classId: string | null;
  open: boolean;
  onClose: () => void;
}

export const ClassDetails: React.FC<ClassDetailsProps> = ({
  classId,
  open,
  onClose,
}) => {
  const styles = useStyles();
  const [classData, setClassData] = useState<EducationClass | null>(null);
  const [teachers, setTeachers] = useState<EducationUser[]>([]);
  const [members, setMembers] = useState<EducationUser[]>([]);
  const [team, setTeam] = useState<EducationTeam | null>(null);
  const [operations, setOperations] = useState<TeamOperation[]>([]);
  const [primaryChannel, setPrimaryChannel] = useState<PrimaryChannel | null>(null);
  const [tabs, setTabs] = useState<ChannelTab[]>([]);
  const [schools, setSchools] = useState<EducationSchool[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && classId) {
      loadClassDetails();
      loadTeachers();
      loadMembers();
      loadTeam();
      loadSchools();
    }
  }, [open, classId]);

  const loadClassDetails = async () => {
    if (!classId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await educationService.getClassById(classId);
      setClassData(response);
    } catch (err: any) {
      console.error('Error loading class details:', err);
      setError(err.message || 'Erro ao carregar detalhes da turma');
    } finally {
      setLoading(false);
    }
  };

  const loadTeachers = async () => {
    if (!classId) return;

    try {
      setLoadingTeachers(true);
      const response = await educationService.getClassTeachers(classId);
      setTeachers(response);
    } catch (err: any) {
      console.error('Error loading teachers:', err);
      // Don't set main error, just log it
    } finally {
      setLoadingTeachers(false);
    }
  };

  const loadMembers = async () => {
    if (!classId) return;

    try {
      setLoadingMembers(true);
      const response = await educationService.getClassMembers(classId);
      setMembers(response);
    } catch (err: any) {
      console.error('Error loading members:', err);
      // Don't set main error, just log it
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadTeam = async () => {
    if (!classId) return;

    try {
      setLoadingTeam(true);
      const teamResponse = await educationService.getClassTeam(classId);
      setTeam(teamResponse);

      if (teamResponse) {
        // Load team operations
        const operationsResponse = await educationService.getClassTeamOperations(classId);
        setOperations(operationsResponse);

        // Load primary channel and tabs
        const channelResponse = await educationService.getClassPrimaryChannel(classId);
        setPrimaryChannel(channelResponse);

        if (channelResponse) {
          const tabsResponse = await educationService.getClassPrimaryChannelTabs(classId);
          setTabs(tabsResponse);
        }
      }
    } catch (err: any) {
      console.error('Error loading team:', err);
      // Don't set main error, just log it
    } finally {
      setLoadingTeam(false);
    }
  };

  const loadSchools = async () => {
    if (!classId) return;

    try {
      setLoadingSchools(true);
      const response = await educationService.getClassSchools(classId);
      setSchools(response);
    } catch (err: any) {
      console.error('Error loading schools:', err);
      // Don't set main error, just log it
    } finally {
      setLoadingSchools(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const renderField = (label: string, value: any) => {
    return (
      <div>
        <div className={styles.fieldLabel}>{label}</div>
        <div className={styles.fieldValue}>{value || '-'}</div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            Detalhes da Turma
          </DialogTitle>

          <DialogContent>
            {loading && (
              <div className={styles.loading}>
                <Spinner label="Carregando detalhes..." />
              </div>
            )}

            {error && (
              <div className={styles.error}>
                <Text>Erro: {error}</Text>
              </div>
            )}

            {!loading && !error && classData && (
              <div className={styles.content}>
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Informações Gerais</div>
                  <div className={styles.fieldRow}>
                    {renderField('Nome da Turma', classData.displayName)}
                    {renderField('Código da Turma', classData.classCode)}
                  </div>
                  <div className={styles.fieldRow}>
                    {renderField('ID Externo', classData.externalId)}
                    {renderField('ID Microsoft', classData.id)}
                  </div>
                  <div className={styles.fieldRow}>
                    {renderField('Email do Grupo', classData.mail)}
                    {renderField('Descrição', classData.description)}
                  </div>
                </div>

                {classData.term && (
                  <div className={styles.section}>
                    <div className={styles.sectionTitle}>Período Letivo</div>
                    <div className={styles.fieldRow}>
                      {renderField('Nome do Período', classData.term.displayName)}
                      {renderField('ID Externo do Período', classData.term.externalId)}
                    </div>
                    <div className={styles.fieldRow}>
                      {renderField('Data de Início', formatDate(classData.term.startDate))}
                      {renderField('Data de Término', formatDate(classData.term.endDate))}
                    </div>
                  </div>
                )}

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Informações de Sincronização</div>
                  <div className={styles.fieldRow}>
                    {renderField('Data de Criação', classData.createdDateTime ? new Date(classData.createdDateTime).toLocaleString('pt-BR') : '-')}
                  </div>
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Professores ({teachers.length})</div>
                  {loadingTeachers ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Spinner size="small" label="Carregando professores..." />
                    </div>
                  ) : teachers.length > 0 ? (
                    <Table className={styles.table} size="small">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell style={{ width: '35%' }}>Nome</TableHeaderCell>
                          <TableHeaderCell style={{ width: '30%' }}>Email</TableHeaderCell>
                          <TableHeaderCell style={{ width: '20%' }}>ID Externo</TableHeaderCell>
                          <TableHeaderCell style={{ width: '15%' }}>Função</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {teachers.map((teacher) => (
                          <TableRow key={teacher.id}>
                            <TableCell>
                              <Text weight="semibold">{teacher.displayName}</Text>
                            </TableCell>
                            <TableCell>{teacher.mail || teacher.userPrincipalName}</TableCell>
                            <TableCell>{teacher.teacher?.externalId || '-'}</TableCell>
                            <TableCell>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px',
                                borderRadius: '5px',
                                backgroundColor: tokens.colorBrandBackground2,
                                color: tokens.colorBrandForeground1,
                              }}>
                                <PersonAccounts20Regular />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <EmptyState variant="inline" message="Nenhum professor encontrado" />
                  )}
                </div>

                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Alunos ({members.length})</div>
                  {loadingMembers ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Spinner size="small" label="Carregando alunos..." />
                    </div>
                  ) : members.length > 0 ? (
                    <Table className={styles.table} size="small">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell style={{ width: '30%' }}>Nome</TableHeaderCell>
                          <TableHeaderCell style={{ width: '27%' }}>Email</TableHeaderCell>
                          <TableHeaderCell style={{ width: '10%' }}>RA</TableHeaderCell>
                          <TableHeaderCell style={{ width: '18%' }}>ID Externo</TableHeaderCell>
                          <TableHeaderCell style={{ width: '15%' }}>Função</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {members.map((member) => (
                          <TableRow key={member.id}>
                            <TableCell>
                              <Text weight="semibold">{member.displayName}</Text>
                            </TableCell>
                            <TableCell>{member.mail || member.userPrincipalName}</TableCell>
                            <TableCell>{member.student?.studentNumber || '-'}</TableCell>
                            <TableCell>{member.student?.externalId || '-'}</TableCell>
                            <TableCell>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px',
                                borderRadius: '5px',
                                backgroundColor: tokens.colorBrandBackground2,
                                color: tokens.colorBrandForeground1,
                              }}>
                                <HatGraduation20Regular />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <EmptyState variant="inline" message="Nenhum aluno encontrado" />
                  )}
                </div>

                {/* Team Section */}
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Microsoft Teams</div>
                  {loadingTeam ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Spinner size="small" label="Carregando informações do Teams..." />
                    </div>
                  ) : team ? (
                    <>
                      <div className={styles.fieldRow}>
                        {renderField('ID do Team', team.id)}
                        {renderField('Especialização', team.specialization || '-')}
                      </div>
                      <div className={styles.fieldRow}>
                        {renderField('URL do Team', team.webUrl ? (
                          <a href={team.webUrl} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1 }}>
                            Abrir no Teams
                          </a>
                        ) : '-')}
                        {renderField('Arquivado', team.isArchived ? 'Sim' : 'Não')}
                      </div>
                      {renderField('Data de Criação', team.createdDateTime ? new Date(team.createdDateTime).toLocaleString('pt-BR') : '-')}

                      {/* Team Operations */}
                      {operations.length > 0 && (
                        <>
                          <div className={styles.sectionTitle} style={{ marginTop: '16px' }}>
                            Operações do Team ({operations.length})
                          </div>
                          <Table className={styles.table} size="small">
                            <TableHeader>
                              <TableRow>
                                <TableHeaderCell>Data</TableHeaderCell>
                                <TableHeaderCell>Tipo de Operação</TableHeaderCell>
                                <TableHeaderCell>Status</TableHeaderCell>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {operations.map((op, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    {op.createdDateTime ? new Date(op.createdDateTime).toLocaleString('pt-BR') : '-'}
                                  </TableCell>
                                  <TableCell>{op.operationType || '-'}</TableCell>
                                  <TableCell>
                                    <Badge
                                      appearance="filled"
                                      color={op.status === 'succeeded' ? 'success' : op.status === 'failed' ? 'danger' : 'warning'}
                                    >
                                      {op.status || '-'}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </>
                      )}
                    </>
                  ) : (
                    <EmptyState variant="inline" message="Esta turma não possui um Team associado" />
                  )}
                </div>

                {/* Primary Channel Section */}
                {primaryChannel && (
                  <div className={styles.section}>
                    <div className={styles.sectionTitle}>Canal Principal</div>
                    <div className={styles.fieldRow}>
                      {renderField('ID do Canal', primaryChannel.id)}
                      {renderField('Nome do Canal', primaryChannel.displayName)}
                    </div>

                    {/* Channel Tabs */}
                    {tabs.length > 0 && (
                      <>
                        <div className={styles.sectionTitle} style={{ marginTop: '16px' }}>
                          Abas do Canal ({tabs.length})
                        </div>
                        <Table className={styles.table} size="small">
                          <TableHeader>
                            <TableRow>
                              <TableHeaderCell>Nome da Aba</TableHeaderCell>
                              <TableHeaderCell>App do Teams</TableHeaderCell>
                              <TableHeaderCell>URL</TableHeaderCell>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {tabs.map((tab) => (
                              <TableRow key={tab.id}>
                                <TableCell>
                                  <Text weight="semibold">{tab.displayName}</Text>
                                </TableCell>
                                <TableCell>{tab.teamsAppId || '-'}</TableCell>
                                <TableCell>
                                  {tab.webUrl ? (
                                    <a href={tab.webUrl} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1 }}>
                                      Abrir
                                    </a>
                                  ) : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    )}
                  </div>
                )}

                {/* Schools Section */}
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Escolas Associadas ({schools.length})</div>
                  {loadingSchools ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                      <Spinner size="small" label="Carregando escolas..." />
                    </div>
                  ) : schools.length > 0 ? (
                    <Table className={styles.table} size="small">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell>Nome da Escola</TableHeaderCell>
                          <TableHeaderCell>Número da Escola</TableHeaderCell>
                          <TableHeaderCell>ID Externo</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {schools.map((school) => (
                          <TableRow key={school.id}>
                            <TableCell>
                              <Text weight="semibold">{school.displayName}</Text>
                            </TableCell>
                            <TableCell>{school.schoolNumber || '-'}</TableCell>
                            <TableCell>{school.externalId || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <EmptyState variant="inline" message="Nenhuma escola associada" />
                  )}
                </div>
              </div>
            )}
          </DialogContent>

          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Fechar
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ClassDetails;
